<template>
  <div class="w-full min-h-[1040px] mt-3 bg-white rounded-t-[20px]">
    <NavbarComponent />

    <div class="mx-8 my-6">
      <div class="space-y-1 mb-6">
        <p class="font-semibold text-bodyText text-[20px]">
          Achievements {{ "&" }} Milestones
        </p>
        <p class="text-bodyText text-[14px]">
          Get a quick snapshot of all your earned Badges for achievements and
          Milestones
        </p>
      </div>

      <div class="mb-6">
        <UserAchievementsCard 
          v-if="!loading"
          :name="fullName"
          :role="userRole"
          :title="userTitle"
          :avatar="userAvatar"
          :milestones="badgesAndMilestones.total_milestones_reached"
          :badgesCollected="badgesAndMilestones.total_badges_earned"
        />
        <div v-else class="flex justify-center items-center h-32">
          <svg class="animate-spin h-8 w-8 text-bodyText" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>

      <div class="border-b border-grayLight">
        <nav class="flex gap-6">
          <button
            @click="activeTab = 'general'"
            :class="[
              'py-3',
              activeTab === 'general'
                ? 'text-bodyText font-medium border-b-2 border-[#4C4D4F]'
                : 'text-[#98A2B3]',
            ]"
          >
            General
          </button>
          <button
            @click="activeTab = 'projects'"
            :class="[
              'py-3',
              activeTab === 'projects'
                ? 'text-bodyText font-medium border-b-2 border-[#4C4D4F]'
                : 'text-[#98A2B3]',
            ]"
          >
            Projects
          </button>
          <button
            @click="activeTab = 'goals'"
            :class="[
              'py-3',
              activeTab === 'goals'
                ? 'text-bodyText font-medium border-b-2 border-[#4C4D4F]'
                : 'text-[#98A2B3]',
            ]"
          >
            Goals
          </button>
          <button
            @click="activeTab = 'community'"
            :class="[
              'py-3',
              activeTab === 'community'
                ? 'text-bodyText font-medium border-b-2 border-[#4C4D4F]'
                : 'text-[#98A2B3]',
            ]"
          >
            Community
          </button>
        </nav>
      </div>

      <div
        class="mt-4 rounded-[12px] bg-white p-3 space-y-3"
        style="border: 1px solid #EEEEEE;"
      >
        <div v-if="achievementsLoading" class="flex justify-center items-center py-8">
          <svg class="animate-spin h-8 w-8 text-bodyText" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <div v-else-if="achievementsError" class="text-red-500 text-center py-4">
          {{ achievementsError }}
        </div>

        <template v-else>
          <template v-if="activeTab === 'general'">
            <div
              v-for="achievement in generalAchievements"
              :key="achievement.achievement_type"
              class="rounded-[12px] p-3"
              style="background-color: #FAFBFC; border: 1px solid #EEEEEE;"
            >
              <AchievementsProgressRow
                :icon="getAchievementIcon(achievement.icon)"
                :title="achievement.name"
                :subtitle="achievement.description"
                :progress="calculateProgress(achievement)"
                :progressText="getProgressText(achievement)"
                :badgeIcon="require(`@/assets/achievements/${achievement.badge}.svg`)"
                :disabled="!achievement.is_completed && achievement.progress === 0"
                compact
              />
            </div>
            <div v-if="generalAchievements.length === 0" class="text-center py-4 text-bodyText">
              No general achievements available
            </div>
          </template>

          <template v-else-if="activeTab === 'projects'">
            <div
              v-for="achievement in projectsAchievements"
              :key="achievement.achievement_type"
              class="rounded-[12px] p-3"
              style="background-color: #FAFBFC; border: 1px solid #EEEEEE;"
            >
              <AchievementsProgressRow
                :icon="getAchievementIcon(achievement.icon)"
                :title="achievement.name"
                :subtitle="achievement.description"
                :progress="calculateProgress(achievement)"
                :progressText="getProgressText(achievement)"
                :badgeIcon="require(`@/assets/achievements/${achievement.badge}.svg`)"
                :disabled="!achievement.is_completed && achievement.progress === 0"
                compact
              />
            </div>
            <div v-if="projectsAchievements.length === 0" class="text-center py-4 text-bodyText">
              No project achievements available
            </div>
          </template>

          <template v-else-if="activeTab === 'goals'">
            <div
              v-for="achievement in goalsAchievements"
              :key="achievement.achievement_type"
              class="rounded-[12px] p-3"
              style="background-color: #FAFBFC; border: 1px solid #EEEEEE;"
            >
              <AchievementsProgressRow
                :icon="getAchievementIcon(achievement.icon)"
                :title="achievement.name"
                :subtitle="achievement.description"
                :progress="calculateProgress(achievement)"
                :progressText="getProgressText(achievement)"
                :badgeIcon="require(`@/assets/achievements/${achievement.badge}.svg`)"
                :disabled="!achievement.is_completed && achievement.progress === 0"
                compact
              />
            </div>
            <div v-if="goalsAchievements.length === 0" class="text-center py-4 text-bodyText">
              No goal achievements available
            </div>
          </template>

          <template v-else>
            <div
              v-for="achievement in communityAchievements"
              :key="achievement.achievement_type"
              class="rounded-[12px] p-3"
              style="background-color: #FAFBFC; border: 1px solid #EEEEEE;"
            >
              <AchievementsProgressRow
                :icon="getAchievementIcon(achievement.icon)"
                :title="achievement.name"
                :subtitle="achievement.description"
                :progress="calculateProgress(achievement)"
                :progressText="getProgressText(achievement)"
                :badgeIcon="require(`@/assets/achievements/${achievement.badge}.svg`)"
                :disabled="!achievement.is_completed && achievement.progress === 0"
                compact
              />
            </div>
            <div v-if="communityAchievements.length === 0" class="text-center py-4 text-bodyText">
              No community achievements available
            </div>
          </template>
        </template>
      </div>

      <!-- Badges section header -->
      <p class="mt-6 mb-3 text-bodyText text-[14px]">Badges</p>

      <div v-if="achievementsLoading" class="flex justify-center items-center py-8">
        <svg class="animate-spin h-8 w-8 text-bodyText" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <div v-else-if="milestonesAchievements.length === 0" class="text-center py-8 text-bodyText">
        No badges available yet
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <AchievementCard
          v-for="achievement in milestonesAchievements"
          :key="achievement.achievement_type"
          :badge="require(`@/assets/achievements/${achievement.badge}.svg`)"
          :title="achievement.name"
          :status="achievement.is_completed ? 'earned' : (calculateProgress(achievement) >= 80 ? 'close' : 'not-earned')"
          :description="achievement.description"
        />
      </div>
    </div>
  </div>
</template>

<script>
import NavbarComponent from "@/components/NavbarComponent.vue";
import AchievementCard from "@/components/achievements/AchievementCard.vue";
import AchievementsProgressRow from "@/components/achievements/AchievementsProgressRow.vue";
import UserAchievementsCard from "@/components/achievements/UserAchievementsCard.vue";
import apiClient from "@/axios";

export default {
  name: "AchievementsComponent",
  components: {
    NavbarComponent,
    AchievementCard,
    AchievementsProgressRow,
    UserAchievementsCard,
  },
  data() {
    return {
      activeTab: "general",
      user: {},
      loading: true,
      achievements: [],
      achievementsLoading: false,
      achievementsError: null,
      badgesAndMilestones: {
        total_badges_earned: 0,
        total_milestones_reached: 0,
      },
    };
  },
  computed: {
    fullName() {
      if (!this.user) return "";
      const firstName = this.user.first_name || "";
      const lastName = this.user.last_name || "";
      return `${firstName} ${lastName}`.trim() || "User";
    },
    userRole() {
      const roles = this.getAvailableRoles();
      if (roles.length === 0) return "User";
      return roles[0].charAt(0).toUpperCase() + roles[0].slice(1);
    },
    userTitle() {
      if (this.user.talent_first_name && this.user.position) {
        return this.user.position;
      }
      return "";
    },
    userAvatar() {
      return this.user.profile_picture || require('@/assets/users/default-profile-picture.jpg');
    },
    generalAchievements() {
      return this.sortAchievements(this.achievements.filter(achievement => achievement.category === 'general'));
    },
    projectsAchievements() {
      return this.sortAchievements(this.achievements.filter(achievement => achievement.category === 'projects'));
    },
    goalsAchievements() {
      return this.sortAchievements(this.achievements.filter(achievement => achievement.category === 'goals'));
    },
    communityAchievements() {
      return this.sortAchievements(this.achievements.filter(achievement => achievement.category === 'community'));
    },
    milestonesAchievements() {
      return this.sortAchievements(this.achievements.filter(achievement => achievement.category === 'milestones'));
    },
  },
  methods: {
    sortAchievements(list) {
      return (list || []).slice().sort((a, b) => {
        const compDone = Number(b?.is_completed) - Number(a?.is_completed);
        if (compDone !== 0) return compDone;
        const progA = this.calculateProgress(a);
        const progB = this.calculateProgress(b);
        const compProg = (progB - progA);
        if (compProg !== 0) return compProg;
        const nameA = (a?.name || '').toString();
        const nameB = (b?.name || '').toString();
        return nameA.localeCompare(nameB);
      });
    },
    async fetchCurrentUser() {
      try {
        this.loading = true;
        const response = await apiClient.post("/current_user");
        this.user = response.data.user || {};
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchAchievements() {
      try {
        this.achievementsLoading = true;
        this.achievementsError = null;
        const response = await apiClient.get("/web/achievements/my-achievements", {
          params: {
            completed_only: false
          }
        });
        this.achievements = response.data || [];
      } catch (error) {
        console.error("Error fetching achievements:", error);
        this.achievementsError = "Failed to load achievements. Please try again.";
      } finally {
        this.achievementsLoading = false;
      }
    },
    async fetchBadgesAndMilestones() {
      try {
        const response = await apiClient.get("/web/achievements/badges-and-milestones");
        this.badgesAndMilestones = response.data || {
          total_badges_earned: 0,
          total_milestones_reached: 0,
        };
      } catch (error) {
        console.error("Error fetching badges and milestones:", error);
      }
    },
    getAvailableRoles() {
      const roles = localStorage.getItem("availableRoles");
      return roles ? JSON.parse(roles) : [];
    },
    calculateProgress(achievement) {
      if (achievement.is_completed) return 100;
      if (!achievement.requirement_count) return 0;
      return Math.round((achievement.progress / achievement.requirement_count) * 100);
    },
    getProgressText(achievement) {
      if (achievement.is_completed) return "Achieved";
      const progressPercent = this.calculateProgress(achievement);
      return `${progressPercent}%`;
    },
    getAchievementIcon(iconName) {
      try {
        return require(`@/assets/achievements/${iconName}.png`);
      } catch (error) {
        console.warn(`Achievement icon not found: ${iconName}`);
        return require('@/assets/award.svg');
      }
    },
  },
  mounted() {
    this.fetchCurrentUser();
    this.fetchAchievements();
    this.fetchBadgesAndMilestones();
  },
};
</script><style scoped>
</style>
